import React from 'react';
import { useCurrentTime } from '@/hooks/useAstronomicalData';

/**
 * AnalogClock - elegant SVG-based analog clock face.
 * Adapts colors based on textColors prop (day/night theming).
 */
const AnalogClock = ({ textColors, surfaceColors }) => {
  const currentTime = useCurrentTime();

  const hours = currentTime.getHours() % 12;
  const minutes = currentTime.getMinutes();
  const seconds = currentTime.getSeconds();
  const ms = currentTime.getMilliseconds();

  // Angles (degrees from 12 o'clock, clockwise)
  const hourAngle = (hours + minutes / 60 + seconds / 3600) * 30;
  const minuteAngle = (minutes + seconds / 60) * 6;
  const secondAngle = (seconds + ms / 1000) * 6;

  // Colors
  const faceText = textColors.primary;
  const accent = textColors.accent;
  const mutedText = textColors.secondary;
  const faceBg = surfaceColors.background;

  // SVG dimensions
  const size = 320;
  const center = size / 2;
  const radius = center - 20;

  // Hour markers
  const hourMarkers = [];
  for (let i = 1; i <= 12; i++) {
    const angle = (i * 30 - 90) * (Math.PI / 180);
    const outer = radius - 6;
    const inner = radius - 22;
    const x1 = center + Math.cos(angle) * outer;
    const y1 = center + Math.sin(angle) * outer;
    const x2 = center + Math.cos(angle) * inner;
    const y2 = center + Math.sin(angle) * inner;
    const isQuarter = i % 3 === 0;
    hourMarkers.push(
      <line
        key={`h-${i}`}
        x1={x1} y1={y1} x2={x2} y2={y2}
        stroke={faceText}
        strokeWidth={isQuarter ? 3 : 1.5}
        strokeLinecap="round"
        opacity={isQuarter ? 1 : 0.6}
      />
    );

    // Numerals
    const numRadius = radius - 42;
    const nx = center + Math.cos(angle) * numRadius;
    const ny = center + Math.sin(angle) * numRadius;
    hourMarkers.push(
      <text
        key={`n-${i}`}
        x={nx} y={ny}
        textAnchor="middle"
        dominantBaseline="central"
        fill={faceText}
        fontSize="20"
        fontFamily="Outfit, sans-serif"
        fontWeight="500"
        opacity={isQuarter ? 1 : 0.65}
      >
        {i}
      </text>
    );
  }

  // Minute markers
  const minuteMarkers = [];
  for (let i = 0; i < 60; i++) {
    if (i % 5 === 0) continue;
    const angle = (i * 6 - 90) * (Math.PI / 180);
    const outer = radius - 6;
    const inner = radius - 12;
    const x1 = center + Math.cos(angle) * outer;
    const y1 = center + Math.sin(angle) * outer;
    const x2 = center + Math.cos(angle) * inner;
    const y2 = center + Math.sin(angle) * inner;
    minuteMarkers.push(
      <line
        key={`m-${i}`}
        x1={x1} y1={y1} x2={x2} y2={y2}
        stroke={mutedText}
        strokeWidth={0.8}
        opacity={0.5}
      />
    );
  }

  return (
    <div
      className="flex flex-col items-center justify-center h-full p-6 md:p-8"
      data-testid="analog-clock"
      dir="ltr"
    >
      <svg
        viewBox={`0 0 ${size} ${size}`}
        className="w-full max-w-[420px] aspect-square"
        style={{ filter: 'drop-shadow(0 8px 32px rgba(0, 0, 0, 0.15))' }}
      >
        {/* Outer rim */}
        <circle
          cx={center} cy={center} r={radius + 4}
          fill="none"
          stroke={faceText}
          strokeWidth="0.5"
          opacity="0.15"
        />

        {/* Inner face */}
        <circle
          cx={center} cy={center} r={radius}
          fill={faceBg}
          opacity="0.4"
        />

        {/* Minute markers */}
        {minuteMarkers}

        {/* Hour markers + numerals */}
        {hourMarkers}

        {/* Brand at 6 o'clock */}
        <text
          x={center}
          y={center + 64}
          textAnchor="middle"
          fill={mutedText}
          fontSize="11"
          fontFamily="Outfit, sans-serif"
          fontWeight="500"
          letterSpacing="3"
          opacity="0.6"
        >
          FALAK
        </text>

        {/* Hour hand */}
        <g transform={`rotate(${hourAngle} ${center} ${center})`}>
          <line
            x1={center} y1={center + 12}
            x2={center} y2={center - radius * 0.5}
            stroke={faceText}
            strokeWidth="5"
            strokeLinecap="round"
          />
        </g>

        {/* Minute hand */}
        <g transform={`rotate(${minuteAngle} ${center} ${center})`}>
          <line
            x1={center} y1={center + 16}
            x2={center} y2={center - radius * 0.75}
            stroke={faceText}
            strokeWidth="3"
            strokeLinecap="round"
          />
        </g>

        {/* Second hand */}
        <g transform={`rotate(${secondAngle} ${center} ${center})`}>
          <line
            x1={center} y1={center + 22}
            x2={center} y2={center - radius * 0.82}
            stroke={accent}
            strokeWidth="1.5"
            strokeLinecap="round"
          />
          <circle
            cx={center} cy={center - radius * 0.7}
            r="4"
            fill={accent}
          />
        </g>

        {/* Center cap */}
        <circle cx={center} cy={center} r="9" fill={faceText} />
        <circle cx={center} cy={center} r="4" fill={accent} />
      </svg>
    </div>
  );
};

export default AnalogClock;
